import { useQuery } from '@tanstack/react-query';
import { useAuth, useUser } from '@/lib/auth';
import { Link, Navigate } from 'react-router-dom';
import { Loader2, Building2, ArrowRight, Plus } from 'lucide-react';
import { api } from '@/lib/api';
import { Empty } from '@/components/Empty';
import { cn } from '@/lib/cn';

/**
 * Page admin Sebastian : liste de tous les clients du Ducens.
 * Inaccessible aux client_user.
 */
export function AdminClientsPage() {
  const { user, isLoaded } = useUser();
  const { getToken } = useAuth();

  if (!isLoaded) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-ducens-muted" />
      </div>
    );
  }

  const isAdmin = user?.publicMetadata?.role === 'admin';
  if (!isAdmin) {
    return <Navigate to={`/clients/${user?.publicMetadata?.default_client_slug ?? 'marie-dupont'}`} replace />;
  }

  return <AdminContent getToken={() => getToken()} />;
}

function AdminContent({ getToken }: { getToken: () => Promise<string | null> }) {
  const clientsQuery = useQuery({
    queryKey: ['admin-clients'],
    queryFn: () => api.adminListClients(getToken),
  });

  return (
    <div className="min-h-screen bg-ducens-bg">
      <header className="border-b border-ducens-border bg-white sticky top-0 z-30">
        <div className="px-6 py-3 flex items-center justify-between max-w-7xl mx-auto">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-ducens-ink flex items-center justify-center text-ducens-accent font-bold">
              F
            </div>
            <div>
              <div className="font-semibold text-sm leading-tight">Ducens</div>
              <div className="text-xs text-ducens-muted leading-tight">Console admin</div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-semibold leading-tight">Tous les clients</h1>
            <p className="text-sm text-ducens-muted mt-1">
              Vue admin Sebastian. Liste exhaustive depuis <code className="px-1 py-0.5 bg-white rounded">public.clients</code>.
            </p>
          </div>
          <Link
            to="/admin/clients/new"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-ducens-ink text-white text-sm font-medium hover:opacity-90"
          >
            <Plus className="w-4 h-4" /> Nouveau client
          </Link>
        </div>

        {clientsQuery.isPending && (
          <div className="flex items-center gap-2 text-ducens-muted">
            <Loader2 className="w-4 h-4 animate-spin" /> Chargement...
          </div>
        )}

        {clientsQuery.data && clientsQuery.data.length === 0 && (
          <Empty Icon={Building2} title="Aucun client" />
        )}

        {clientsQuery.data && clientsQuery.data.length > 0 && (
          <div className="space-y-2">
            {clientsQuery.data.map((c) => (
              <Link
                key={c.id}
                to={`/clients/${c.schema_isolation}`}
                className="group bg-white border border-ducens-border rounded-lg px-5 py-4 flex items-center gap-4 hover:border-ducens-ink transition-colors"
              >
                <div className="w-10 h-10 rounded-lg bg-ducens-bg flex items-center justify-center shrink-0">
                  <Building2 className="w-5 h-5 text-ducens-muted" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <div className="font-medium">{c.company_name}</div>
                    <HealthBadge status={c.health_status} />
                    <PlanBadge tier={c.plan_tier} />
                  </div>
                  <div className="text-sm text-ducens-muted mt-0.5 truncate">
                    {c.contact_email} · {c.niche}
                  </div>
                </div>
                <div className="hidden md:flex flex-col items-end gap-0.5 shrink-0 text-right">
                  <span className="text-sm font-medium tabular-nums">{c.mrr_eur} €/mois</span>
                  <span className="text-xs text-ducens-muted">depuis {c.started_at}</span>
                </div>
                <ArrowRight className="w-4 h-4 text-ducens-muted group-hover:text-ducens-ink transition-colors" />
              </Link>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

function HealthBadge({ status }: { status: string }) {
  const map: Record<string, string> = {
    green: 'bg-emerald-100 text-emerald-900',
    yellow: 'bg-amber-100 text-amber-900',
    red: 'bg-red-100 text-red-900',
  };
  return <span className={cn('inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium', map[status] ?? '')}>{status}</span>;
}

function PlanBadge({ tier }: { tier: string }) {
  return (
    <span className="inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium bg-ducens-bg text-ducens-muted border border-ducens-border">
      {tier}
    </span>
  );
}
